import SimpleModal from "app/components/Modals/SimpleModal";
import { WeeklyCheckInActivityEmailReportTableData } from "../types";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getWeeklyCheckInActivityEmailReportById,
  selectGetWeeklyCheckInActivityEmailReportByIdStatus,
  selectSampleWeeklyCheckInActivityData,
  selectWeeklyCheckInActivityEmailReports,
} from "../slice";
import {
  selectAllCompanyUsersById,
  selectSampleUsersInfoById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { getScoreSpan, getUserInfo } from "../helpers";
import { Card, Dropdown } from "react-bootstrap";
import { CHECK_IN_EMAIL_REPORT_TYPES } from "../constants";
import Loading from "app/storybookComponents/Loading";

interface CheckInEmailReportModalProps {
  onHide: () => void;
  show: boolean;
  userAccountId?: number | null;
  selectReports?: WeeklyCheckInActivityEmailReportTableData["reports"];
  initialReportIndex?: number;
  date?: string;
  isSample?: boolean;
}

const CheckInEmailReportModal = ({
  show,
  onHide,
  userAccountId,
  selectReports = [],
  initialReportIndex = 0,
  date,
  isSample,
}: Readonly<CheckInEmailReportModalProps>) => {
  const dispatch = useAppDispatch();
  const sampleWeeklyCheckInActivityData = useAppSelector(
    selectSampleWeeklyCheckInActivityData
  );
  const companyUsers = useAppSelector(selectAllCompanyUsersById);
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const weeklyCheckInActivityEmailReports = useAppSelector(
    selectWeeklyCheckInActivityEmailReports
  );
  const weeklyCheckInActivityEmailReportByIdStatus = useAppSelector(
    selectGetWeeklyCheckInActivityEmailReportByIdStatus
  );
  const [indexShowing, setIndexShowing] = useState(initialReportIndex);
  const usersById = useMemo(() => {
    if (isSample) {
      return sampleUsersById;
    }
    return companyUsers;
  }, [isSample, sampleUsersById, companyUsers]);

  const reportShowing = useMemo(() => {
    return selectReports[indexShowing];
  }, [selectReports, indexShowing]);

  const reportData = useMemo(() => {
    if (!reportShowing) {
      return null;
    }
    if (isSample) {
      return sampleWeeklyCheckInActivityData?.checkInEmailReportMap?.[
        reportShowing.checkInEmailReportId
      ];
    }

    return weeklyCheckInActivityEmailReports[
      reportShowing.checkInEmailReportId
    ];
  }, [
    reportShowing,
    weeklyCheckInActivityEmailReports,
    isSample,
    sampleWeeklyCheckInActivityData,
  ]);

  // Add a useEffect to handle modal close
  useEffect(() => {
    if (!show) {
      setIndexShowing(initialReportIndex);
    }
  }, [show, initialReportIndex]);

  useEffect(() => {
    if (!reportShowing || isSample) {
      return;
    }

    dispatch(
      getWeeklyCheckInActivityEmailReportById(
        reportShowing.checkInEmailReportId
      )
    );
  }, [reportShowing, dispatch, isSample]);

  const getTitle = () => {
    if (!userAccountId) {
      return null;
    }

    return (
      <div className="column-gap-8px">
        <p>Email report(s) sent to</p>
        <div className="row-gap-8px justify-content-between">
          {getUserInfo(usersById, userAccountId)}
          {getReportDropdown()}
        </div>
      </div>
    );
  };

  const getBody = () => {
    const isLoading =
      reportShowing &&
      weeklyCheckInActivityEmailReportByIdStatus[
        reportShowing.checkInEmailReportId
      ] === "loading";

    if (isLoading) {
      return <Loading />;
    }

    if (!reportData) {
      return null;
    }

    return (
      <>
        <div className="row-gap-16px">
          {getScoreSpan("Avg. Mood score", reportData?.averageMoodScore)}
          {getScoreSpan(
            "Avg. Progress score",
            reportData?.averageProgressScore
          )}
          {getScoreSpan(
            "Avg. Readiness score",
            reportData?.averageReadinessScore
          )}
        </div>
        <Card
          className={`padding-20px raw-email-html ${
            isSample ? "is-sample" : ""
          }`}
        >
          <div
            dangerouslySetInnerHTML={{ __html: reportData.rawHtmlReportText }}
          />
        </Card>
      </>
    );
  };

  const getReportDropdown = () => {
    if (selectReports.length < 2) {
      return null;
    }

    const selectedReport = reportShowing
      ? CHECK_IN_EMAIL_REPORT_TYPES[reportShowing.reportType]
      : null;
    return (
      <Dropdown
        onSelect={(index) => {
          setIndexShowing(Number(index));
        }}
      >
        <Dropdown.Toggle
          id="dropdown-basic"
          className="d-flex align-items-center justify-content-between"
          style={{ width: "200px" }}
          variant="light"
        >
          {selectedReport}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {selectReports.map((report, idx) => (
            <React.Fragment key={report.checkInEmailReportId}>
              {getReportDropdownOption(report, idx)}
              {selectReports.length - 1 !== idx && <Dropdown.Divider />}
            </React.Fragment>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getReportDropdownOption = (
    report: WeeklyCheckInActivityEmailReportTableData["reports"][number],
    index: number
  ) => {
    let reportName = CHECK_IN_EMAIL_REPORT_TYPES[report.reportType];
    const team = report.teamId ? teamsById[report.teamId] : null;
    if (team) {
      reportName += ` - ${team.teamName}`;
    }
    return (
      <Dropdown.Item key={report.checkInEmailReportId} eventKey={index}>
        {reportName}
      </Dropdown.Item>
    );
  };

  const getModalTitle = () => {
    const user = userAccountId ? usersById[userAccountId] : null;
    if (!reportShowing || !user) {
      return "Email Report";
    }
    const { firstName, lastName } = user;
    const name = `${firstName} ${lastName}`.trim();
    const reportType = CHECK_IN_EMAIL_REPORT_TYPES[reportShowing.reportType];
    if (!date) {
      return `${name}'s ${reportType} Report`;
    }

    const dateString = new Date(date).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
    });
    return `${name}'s ${dateString} ${reportType} Report`;
  };

  return (
    <SimpleModal show={show} onHide={onHide} title={getModalTitle()}>
      <hr className="m-0" />
      {getTitle()}
      <hr className="m-0" />
      {getBody()}
    </SimpleModal>
  );
};

export default CheckInEmailReportModal;
